// Deep import, not the `@firmable/utilities` barrel: the barrel pulls in
// @firmable/contracts, which relies on ambient globals each app declares for
// itself. This module is self-contained and has no imports of its own.
import { ClientSource, type Source } from '@firmable/utilities/src/Constants/ClientSource';

export type ClientSourceValue = Source;

/** `x-fbl-client-source` — already set by every client. Reused, not re-invented. */
export const CLIENT_SOURCE_HEADER = ClientSource.HEADER;
export const isClientSource = ClientSource.is;

/**
 * Firmable span attributes.
 *
 * Namespaced `firmable.*` so `attributes->>'firmable.org_id'` in Logfire SQL can
 * never collide with OTel semantic conventions — and so the scrubber can treat
 * the whole namespace as curated-and-safe in one rule.
 *
 * Every value here is an id, a count, a status or a bounded enum. If you find
 * yourself wanting to add free text, add a hash or a count instead.
 */
export const Attr = {
  CLIENT_SOURCE: 'firmable.client_source',
  ORG_ID: 'firmable.org_id',
  USER_ID: 'firmable.user_id',
  DEPLOYMENT_ID: 'firmable.deployment_id',
  RUNTIME: 'firmable.runtime',

  /** `x-fbl-request-origin`, e.g. `app-search-v2`. Split into the two below as well. */
  REQUEST_ORIGIN: 'firmable.request_origin',
  FEATURE: 'firmable.feature',
  REQUEST_ID: 'firmable.request_id',
  OPERATION: 'firmable.operation',

  SEARCH_FILTER_KEYS: 'firmable.search.filter_keys',
  SEARCH_HIT_COUNT: 'firmable.search.hit_count',
  SEARCH_REQUEST_BYTES: 'firmable.search.request_bytes',
  /** `field.path: message` pairs from a Search API 422. Never the rejected values themselves. */
  SEARCH_VALIDATION_ERRORS: 'firmable.search.validation_errors',

  USAGE_KIND: 'firmable.usage.kind',
  USAGE_REQUESTED: 'firmable.usage.requested',
  USAGE_INSERTED: 'firmable.usage.inserted',
  CREDITS_CHARGED: 'firmable.credits.charged',
  CREDITS_FROM_SUBSCRIPTION: 'firmable.credits.from_subscription',
  CREDITS_FROM_ADDON: 'firmable.credits.from_addon',
  CREDITS_INSUFFICIENT: 'firmable.credits.insufficient',
  PROFILE_COUNT: 'firmable.profile.count',
  CATEGORY: 'firmable.category',

  /** Signal agent id. NOT carried by baggage, unlike org/user — pass it explicitly. */
  AGENT_ID: 'firmable.agent_id',

  /**
   * The id-shaped path segments that templating replaced with `:id`, so
   * `/company/f000001729377` is still answerable as "which company?" without
   * putting the id into the indexed span name.
   */
  PATH_IDS: 'firmable.path_ids',

  CRM_PROVIDER: 'firmable.crm.provider',
  PARTNER: 'firmable.partner',

  /** Extension build version and the MV3 surface an error came from. */
  EXTENSION_VERSION: 'firmable.extension.version',
  EXTENSION_SURFACE: 'firmable.extension.surface',

  TOOL_NAME: 'firmable.tool.name',
  TOOL_STATUS: 'firmable.tool.status',
  INPUT_SHAPE: 'firmable.input.shape',
  OUTPUT_SHAPE: 'firmable.output.shape',

  /**
   * The status a route is returning from a terminal `catch`.
   *
   * Distinct from `http.response.status_code` on purpose: that attribute is also
   * written by outbound client spans (`SearchApi/makeRequest.ts`), so lifting it
   * onto the root would report an upstream 500 the route recovered from. This one
   * is written in exactly one place, `jsonError`, so its presence means "a handler
   * caught an exception and gave up".
   */
  HANDLED_ERROR_STATUS: 'firmable.handled_error.status',

  /** Makes the scrubber itself observable — alert if this ever drops to zero. */
  SCRUBBED_COUNT: 'firmable.scrubbed_count',
  /** Forces the sampler to keep a span regardless of the trace ratio. */
  FORCE_SAMPLE: 'firmable.force_sample',
} as const;

/** Baggage keys carried from the edge proxy into every downstream span. */
export const BaggageKey = {
  ORG_ID: Attr.ORG_ID,
  USER_ID: Attr.USER_ID,
  CLIENT_SOURCE: Attr.CLIENT_SOURCE,
} as const;

export const BAGGAGE_KEYS: readonly string[] = [BaggageKey.ORG_ID, BaggageKey.USER_ID, BaggageKey.CLIENT_SOURCE];
